/**
 * <VerificationChooser /> — Universal verification UI.
 *
 * Drop this anywhere we need to prove someone owns an email or phone:
 * login, forgot-password, parent mobile/email verification on signup,
 * enquiry/admission forms, profile mobile/email change, authorised
 * pickup confirmation, sensitive account changes.
 *
 * Channels
 * ────────
 *   • email_otp     — 6-digit code to the email (Brevo)
 *   • sms_otp       — 6-digit code to the mobile (Brevo Transactional SMS)
 *   • whatsapp_scan — user sends "MANGOCUBS VERIFY {token}" from THEIR
 *                      WhatsApp to the MangoCubs business number; our
 *                      AiSensy webhook (or, until that's live, a manual
 *                      admin click) verifies the session server-side.
 *
 * Responsive WhatsApp behaviour
 * ─────────────────────────────
 *   • Desktop (pointer:fine) → QR code primary, "Open WhatsApp Web"
 *     button secondary.
 *   • Mobile / Tablet (pointer:coarse) → big "Verify using WhatsApp"
 *     button primary, optional QR shown as fallback.
 *
 * Props
 * ─────
 *   purpose         (string, required) — one of the Purpose literals defined on
 *                                          the backend (see core/verification.py).
 *   defaultChannel  (string, optional) — preselect a channel.
 *   defaultMobile   (string, optional) — preseed the phone input (E.164).
 *   defaultEmail    (string, optional) — preseed the email input.
 *   userId, applicantId, studentId, branchId, franchiseId, displayName,
 *   metadata        (passthrough scope hints stored on the session).
 *   allowedChannels (array of channel ids, optional) — limit which
 *                    channels are offered (defaults to all 3).
 *   onVerified      (function, required) — called with the verified
 *                    session payload when the backend flips status to
 *                    "verified". Caller decides what to do next (e.g.
 *                    submit the form, advance the wizard, …).
 *   onCancel        (function, optional) — bound to a "Cancel" link.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  EnvelopeSimple, WhatsappLogo, ChatCircleDots, CheckCircle,
  ArrowsClockwise, Timer, Question, ArrowRight,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { isTestMode } from "@/lib/envMode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CHANNEL_META = {
  email_otp:     { label: "Email OTP",         icon: EnvelopeSimple, identifierKey: "email"  },
  sms_otp:       { label: "SMS OTP",           icon: ChatCircleDots, identifierKey: "mobile" },
  whatsapp_scan: { label: "WhatsApp Verify",   icon: WhatsappLogo,   identifierKey: "mobile" },
};

const POLL_INTERVAL_MS = 3000;
const SECONDS_PER_MIN = 60;

function useCountdown(deadline) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!deadline) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [deadline]);
  if (!deadline) return null;
  const remaining = Math.max(0, Math.floor((deadline - now) / 1000));
  const mm = Math.floor(remaining / SECONDS_PER_MIN);
  const ss = remaining % SECONDS_PER_MIN;
  return { remaining, label: `${mm}:${String(ss).padStart(2, "0")}` };
}

function useTouchOrSmall() {
  // True for touch devices (phone / tablet) OR narrow viewports (so a
  // desktop browser resized to mobile width also shows the big-button
  // layout). Combines `matchMedia("(pointer:coarse)")` with a
  // `(max-width: 767px)` check — either flips the UI to mobile-first.
  function compute() {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return (
      window.matchMedia("(pointer:coarse)").matches
      || window.matchMedia("(max-width: 767px)").matches
    );
  }
  const [coarse, setCoarse] = useState(compute);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const pointerMq = window.matchMedia("(pointer:coarse)");
    const widthMq   = window.matchMedia("(max-width: 767px)");
    const handler = () => setCoarse(compute());
    const subscribe = (mq) => {
      if (mq.addEventListener) mq.addEventListener("change", handler);
      else mq.addListener(handler);
    };
    const unsubscribe = (mq) => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
    subscribe(pointerMq);
    subscribe(widthMq);
    return () => { unsubscribe(pointerMq); unsubscribe(widthMq); };
  }, []);
  return coarse;
}

export default function VerificationChooser({
  purpose,
  defaultChannel = "email_otp",
  defaultMobile = "",
  defaultEmail = "",
  userId,
  applicantId,
  studentId,
  branchId,
  franchiseId,
  displayName,
  metadata,
  allowedChannels = ["email_otp", "sms_otp", "whatsapp_scan"],
  onVerified,
  onCancel,
}) {
  const [channel, setChannel] = useState(
    allowedChannels.includes(defaultChannel) ? defaultChannel : allowedChannels[0],
  );
  const [email, setEmail] = useState(defaultEmail);
  const [mobile, setMobile] = useState(defaultMobile);
  const [phase, setPhase] = useState("collect"); // collect → in_flight → verified
  const [session, setSession] = useState(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const pollTimer = useRef(null);
  const coarse = useTouchOrSmall();
  const testMode = isTestMode();

  // Cleanup polling on unmount or channel change.
  useEffect(() => () => {
    if (pollTimer.current) clearInterval(pollTimer.current);
  }, []);

  const expiresAtMs = useMemo(
    () => (session?.expires_at ? Date.parse(session.expires_at) : null),
    [session?.expires_at],
  );
  const countdown = useCountdown(expiresAtMs);

  function reset() {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    setSession(null);
    setOtp("");
    setPhase("collect");
    setError("");
    setInfo("");
  }

  function startPolling(sessionId) {
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollTimer.current = setInterval(async () => {
      try {
        const { data } = await api.get(
          `/verification/session/${sessionId}/status`,
        );
        if (data.status === "verified") {
          clearInterval(pollTimer.current);
          pollTimer.current = null;
          setPhase("verified");
          setSession((prev) => ({ ...prev, ...data }));
          if (onVerified) onVerified(data);
        } else if (data.status === "expired" || data.status === "cancelled") {
          clearInterval(pollTimer.current);
          pollTimer.current = null;
          setError(
            data.status === "expired"
              ? "This verification expired. Please regenerate the link."
              : "Verification cancelled.",
          );
        }
      } catch (err) {
        // Soft failure — keep polling. Don't surface transient errors.
        if (window?.console) window.console.warn("verification poll error", err);
      }
    }, POLL_INTERVAL_MS);
  }

  async function start() {
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const payload = {
        channel, purpose,
        user_id: userId, applicant_id: applicantId, student_id: studentId,
        branch_id: branchId, franchise_id: franchiseId,
        display_name: displayName, metadata,
      };
      if (channel === "email_otp") payload.email = email.trim();
      else payload.mobile = mobile.trim();
      const { data } = await api.post("/verification/start", payload);
      setSession(data);
      setPhase("in_flight");
      if (data.rate_limited) {
        setInfo(
          "We just sent a code recently. Please wait a minute before trying again.",
        );
      }
      if (channel === "whatsapp_scan") {
        startPolling(data.session_id);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) ||
                "Could not start verification. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyOtp(e) {
    e?.preventDefault?.();
    if (!session || otp.length !== 6) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/verification/verify-otp", {
        session_id: session.session_id, code: otp,
      });
      setPhase("verified");
      setSession((prev) => ({ ...prev, ...data }));
      if (onVerified) onVerified(data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) ||
                "Invalid or expired code. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (!session) return;
    setBusy(true); setError(""); setInfo("");
    try {
      const { data } = await api.post("/verification/resend", {
        session_id: session.session_id,
      });
      setSession(data);
      setOtp("");
      if (channel === "whatsapp_scan") {
        startPolling(data.session_id);
        setInfo("New verification link generated. Send it to MangoCubs OS™.");
      } else {
        setInfo("New code sent.");
      }
      if (data.rate_limited) {
        setInfo("Please wait a minute before trying again.");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) ||
                "Couldn't regenerate. Please wait a minute.");
    } finally {
      setBusy(false);
    }
  }

  async function manualPollOnce() {
    if (!session) return;
    try {
      const { data } = await api.get(
        `/verification/session/${session.session_id}/status`,
      );
      setSession((prev) => ({ ...prev, ...data }));
      if (data.status === "verified") {
        if (pollTimer.current) clearInterval(pollTimer.current);
        setPhase("verified");
        if (onVerified) onVerified(data);
      } else {
        setInfo("Still waiting — we'll keep checking automatically every few seconds.");
      }
    } catch (err) {
      if (window?.console) window.console.warn("manual poll error", err);
    }
  }

  function switchTo(nextChannel) {
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollTimer.current = null;
    setSession(null);
    setOtp("");
    setError("");
    setInfo("");
    setPhase("collect");
    setChannel(nextChannel);
  }

  // ── Render ───────────────────────────────────────────────────────────
  if (phase === "verified") {
    return (
      <div data-testid="verification-success"
            className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
        <div className="flex items-center gap-3 text-emerald-700">
          <CheckCircle weight="fill" size={28} />
          <div>
            <div className="font-semibold">Verified successfully</div>
            <div className="text-sm">
              {session?.masked || ""} confirmed via{" "}
              {(session?.verified_by_channel || channel).replace(/_/g, " ")}.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="verification-chooser"
          className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6 shadow-sm">
      {/* Channel switcher (always visible so user can switch even after start) */}
      <ChannelSwitcher
        allowedChannels={allowedChannels}
        active={channel}
        onChange={switchTo}
      />

      {phase === "collect" && (
        <CollectStep
          channel={channel}
          email={email} setEmail={setEmail}
          mobile={mobile} setMobile={setMobile}
          onSubmit={start}
          busy={busy}
        />
      )}

      {phase === "in_flight" && channel !== "whatsapp_scan" && (
        <OtpStep
          channel={channel}
          masked={session?.masked}
          otp={otp} setOtp={setOtp}
          onSubmit={verifyOtp}
          onResend={resend}
          countdownLabel={countdown?.label}
          busy={busy}
          debugOtp={testMode ? session?.debug_otp : null}
        />
      )}

      {phase === "in_flight" && channel === "whatsapp_scan" && (
        <WhatsAppScanStep
          session={session}
          coarse={coarse}
          onResend={resend}
          onCheck={manualPollOnce}
          countdownLabel={countdown?.label}
          busy={busy}
        />
      )}

      {(error || info) && (
        <div className="mt-3 text-sm space-y-1">
          {error && (
            <div data-testid="verification-error"
                  className="text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}
          {info && (
            <div data-testid="verification-info"
                  className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              {info}
            </div>
          )}
        </div>
      )}

      {onCancel && (
        <div className="mt-4 text-sm">
          <button
            data-testid="verification-cancel-link"
            onClick={() => { reset(); onCancel(); }}
            className="text-stone-500 hover:text-stone-800 underline">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────
function ChannelSwitcher({ allowedChannels, active, onChange }) {
  return (
    <div className="flex flex-wrap gap-2 mb-5"
          data-testid="verification-channel-switcher">
      {allowedChannels.map((c) => {
        const meta = CHANNEL_META[c];
        if (!meta) return null;
        const Icon = meta.icon;
        const isActive = c === active;
        return (
          <button
            key={c}
            type="button"
            data-testid={`verification-channel-${c}`}
            onClick={() => onChange(c)}
            className={[
              "flex-1 min-w-[120px] inline-flex items-center justify-center gap-2",
              "px-3 py-2 rounded-full text-sm font-medium border transition-all",
              isActive
                ? "bg-stone-900 text-white border-stone-900 shadow"
                : "bg-white text-stone-700 border-stone-200 hover:border-stone-400",
            ].join(" ")}>
            <Icon size={16} weight={isActive ? "fill" : "regular"} />
            {meta.label}
          </button>
        );
      })}
    </div>
  );
}

function CollectStep({
  channel, email, setEmail, mobile, setMobile, onSubmit, busy,
}) {
  const meta = CHANNEL_META[channel];
  return (
    <form
      data-testid={`verification-collect-${channel}`}
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      className="space-y-3">
      <label className="block text-sm font-medium text-stone-700">
        {channel === "email_otp"
          ? "Email address"
          : "Mobile number (with country code)"}
      </label>
      {channel === "email_otp" ? (
        <Input
          type="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="verification-email-input"
        />
      ) : (
        <Input
          type="tel"
          required
          placeholder="+91 98441 39333"
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          data-testid="verification-mobile-input"
        />
      )}
      <Button
        type="submit"
        disabled={busy ||
          (channel === "email_otp" ? !email.trim() : !mobile.trim())}
        data-testid="verification-start-button"
        className="w-full">
        {busy ? "Sending..." : (
          <>
            {channel === "whatsapp_scan"
              ? "Generate WhatsApp link"
              : `Send ${meta?.label || "code"}`} <ArrowRight size={16} className="ml-1" />
          </>
        )}
      </Button>
      <div className="text-xs text-stone-500 flex items-start gap-1.5">
        <Question size={14} className="mt-0.5 shrink-0" />
        <span>
          {channel === "whatsapp_scan"
            ? "You'll send a quick pre-filled message from your WhatsApp. We'll auto-verify it."
            : `We'll send a 6-digit code to your ${
                channel === "email_otp" ? "inbox" : "phone"
              } — it expires in 10 minutes.`}
        </span>
      </div>
    </form>
  );
}

function OtpStep({
  channel, masked, otp, setOtp, onSubmit, onResend, countdownLabel,
  busy, debugOtp,
}) {
  // Auto-focus + auto-submit when 6 digits entered.
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus?.(); }, []);
  return (
    <form
      data-testid={`verification-otp-${channel}`}
      onSubmit={onSubmit}
      className="space-y-4">
      <div className="text-sm text-stone-700">
        Enter the 6-digit code we sent to{" "}
        <span className="font-semibold">{masked}</span>.
      </div>
      <Input
        ref={inputRef}
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={6}
        value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="123456"
        data-testid="verification-otp-input"
        className="text-center text-2xl tracking-[0.4em] font-semibold"
      />
      {debugOtp && (
        <div data-testid="verification-debug-otp"
              className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1 text-amber-800">
          <b>Preview only</b> · OTP: <code>{debugOtp}</code>
        </div>
      )}
      <Button
        type="submit"
        disabled={busy || otp.length !== 6}
        data-testid="verification-otp-verify"
        className="w-full">
        {busy ? "Verifying..." : "Verify"}
      </Button>
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1 text-stone-500">
          <Timer size={14} />
          {countdownLabel ? `Expires in ${countdownLabel}` : "—"}
        </div>
        <button
          type="button"
          onClick={onResend}
          disabled={busy}
          data-testid="verification-resend"
          className="text-stone-700 hover:text-stone-900 underline inline-flex items-center gap-1">
          <ArrowsClockwise size={14} /> Resend code
        </button>
      </div>
    </form>
  );
}

function WhatsAppScanStep({
  session, coarse, onResend, onCheck, countdownLabel, busy,
}) {
  if (!session) return null;
  const waLink = session.wa_link;
  const scanMessage = session.scan_message;
  const debugToken = isTestMode() ? session.debug_token : null;

  return (
    <div data-testid="verification-whatsapp-scan" className="space-y-4">
      <div className="text-sm text-stone-700">
        Verify <span className="font-semibold">{session.masked}</span> by
        sending a quick WhatsApp message to MangoCubs OS™.
      </div>

      {coarse ? (
        // Mobile / Tablet — primary action is the big button.
        <MobileScanLayout
          waLink={waLink} scanMessage={scanMessage} onCheck={onCheck} busy={busy}
        />
      ) : (
        // Desktop — primary is the QR; "Open WhatsApp Web" secondary.
        <DesktopScanLayout
          waLink={waLink} scanMessage={scanMessage}
        />
      )}

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1 text-stone-500">
          <Timer size={14} />
          {countdownLabel ? `Expires in ${countdownLabel}` : "—"}
        </div>
        <button
          type="button"
          onClick={onResend}
          disabled={busy}
          data-testid="verification-whatsapp-regenerate"
          className="text-stone-700 hover:text-stone-900 underline inline-flex items-center gap-1">
          <ArrowsClockwise size={14} /> Regenerate link
        </button>
      </div>

      {session.scan_manual_mode && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded p-3 text-amber-900"
              data-testid="verification-manual-mode-note">
          <b>Heads up:</b> WhatsApp auto-verify is being set up. If the page
          doesn&apos;t advance automatically, our team will confirm your number
          shortly after you send the message.
        </div>
      )}

      {debugToken && (
        <div data-testid="verification-debug-token"
              className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1 text-amber-800">
          <b>Preview only</b> · Token: <code>{debugToken}</code>
        </div>
      )}
    </div>
  );
}

function DesktopScanLayout({ waLink, scanMessage }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4 items-center">
      <div className="flex flex-col items-center gap-2 p-4 bg-stone-50 rounded-xl border border-stone-200">
        {waLink && (
          <div data-testid="verification-qr">
            <QRCodeSVG value={waLink} size={160} level="M" includeMargin />
          </div>
        )}
        <div className="text-xs text-stone-500 text-center">
          Scan with your registered WhatsApp number
        </div>
      </div>
      <ol className="text-sm space-y-2 text-stone-700">
        <li><b>1.</b> Open WhatsApp on your phone.</li>
        <li><b>2.</b> Scan the QR — a pre-filled message will appear.</li>
        <li><b>3.</b> Tap <b>Send</b>. We&apos;ll verify automatically.</li>
      </ol>
      <a
        href={waLink} target="_blank" rel="noopener noreferrer"
        data-testid="verification-whatsapp-web-link"
        className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition">
        <WhatsappLogo size={18} weight="fill" /> Open WhatsApp Web instead
      </a>
      <div className="sm:col-span-2 text-xs text-stone-500 text-center">
        Message we&apos;ll send: <code className="bg-stone-100 px-1 py-0.5 rounded">{scanMessage}</code>
      </div>
    </div>
  );
}

function MobileScanLayout({ waLink, scanMessage, onCheck, busy }) {
  return (
    <div className="space-y-3">
      <a
        href={waLink}
        data-testid="verification-whatsapp-mobile-button"
        className="block w-full text-center py-4 rounded-2xl bg-emerald-600 text-white text-base font-semibold shadow-sm hover:bg-emerald-700 transition">
        <WhatsappLogo size={22} weight="fill" className="inline -mt-1 mr-2" />
        Verify using WhatsApp
      </a>
      <ol className="text-sm space-y-1.5 text-stone-700 pl-1">
        <li><b>1.</b> Tap the button above.</li>
        <li><b>2.</b> WhatsApp will open with a pre-filled message.</li>
        <li><b>3.</b> Tap <b>Send</b>.</li>
        <li><b>4.</b> Come back here — we&apos;ll verify automatically.</li>
      </ol>
      <Button
        type="button"
        variant="outline"
        onClick={onCheck}
        disabled={busy}
        data-testid="verification-mobile-check"
        className="w-full">
        I&apos;ve sent the message — check now
      </Button>
      <div className="text-xs text-stone-500 text-center">
        Message: <code className="bg-stone-100 px-1 py-0.5 rounded">{scanMessage}</code>
      </div>
    </div>
  );
}
